import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../AdminManageCategories/AdminManageCategories.css';
import { buildCategoryTree } from '../../firebase/categories';
import { useCategoryManagement } from '../../hooks/useCategoryManagement';

function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label: 'Manage Products', path: '/admin/products/manage' },
        { label: 'Manage Category', path: '/admin/products/categories' },
        { label: 'Manage Subcategory', path: '/admin/products/subcategories' },
        { label: 'Manage Submenus', path: '/admin/products/submenus' },
        { label: 'Manage Attributes', path: '/admin/products/attributes' },
        { label: 'Manage Inventory', path: '/admin/products/inventory' },
    ];
    return <div className="mc-tab-bar">{tabs.map((tab) => <button key={tab.path} className={`mc-tab${active === tab.label ? ' mc-tab--active' : ''}`} onClick={() => navigate(tab.path)}>{tab.label}</button>)}</div>;
}

function StatusToggle({ status, onChange }) {
    const checked = status === 'active';
    return (
        <label className="mc-toggle">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="mc-toggle-track"><span className="mc-toggle-thumb" /></span>
        </label>
    );
}

function StatusBadge({ status }) {
    return <span className={`mc-status-badge mc-status-badge--${status || 'active'}`}>{status === 'inactive' ? 'Inactive' : 'Active'}</span>;
}

function SubcategoryModal({ subcategory, mainCategories, onClose, onSave }) {
    const [form, setForm] = useState({
        name: subcategory?.name || '',
        description: subcategory?.description || '',
        imageUrl: subcategory?.imageUrl || '',
        status: subcategory?.status || 'active',
        parentId: subcategory?.parentId || mainCategories[0]?.id || '',
    });
    const selectedParent = mainCategories.find((category) => category.id === form.parentId);

    const submit = () => {
        if (!form.name.trim() || !form.description.trim() || !form.parentId) return;
        onSave({ ...form, parentName: selectedParent?.name || '' });
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(event) => event.stopPropagation()}>
                <div className="mc-modal-header">
                    <h2 className="mc-modal-title">{subcategory ? 'Edit Subcategory' : 'Add Subcategory'}</h2>
                    <button className="mc-modal-close" onClick={onClose}>×</button>
                </div>
<div className="mc-modal-body">
                    <div className="mc-field"><label>Name <span className="mc-required">*</span></label><input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} /></div>
                    <div className="mc-field"><label>Description <span className="mc-required">*</span></label><textarea rows="4" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} /></div>
                    <div className="mc-field"><label>Optional Image URL</label><input value={form.imageUrl} onChange={(event) => setForm((prev) => ({ ...prev, imageUrl: event.target.value }))} /></div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div className="mc-field"><label>Parent Main Category</label><select value={form.parentId} onChange={(event) => setForm((prev) => ({ ...prev, parentId: event.target.value }))}>{mainCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></div>
                        <div className="mc-field"><label>Status</label><StatusToggle status={form.status} onChange={() => setForm((prev) => ({ ...prev, status: prev.status === 'active' ? 'inactive' : 'active' }))} /></div>
                    </div>
                </div>
                <div className="mc-modal-footer"><button className="mc-btn-secondary" onClick={onClose}>Cancel</button><button className="mc-btn-primary" onClick={submit}>Save Subcategory</button></div>
            </div>
        </div>
    );
}

export default function AdminManageSubcategories() {
    const { categories, loading, error, fetchCategories, createSubcategoryEntry, updateCategoryEntry, deleteCategoryEntry } = useCategoryManagement([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [modal, setModal] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const tree = useMemo(() => buildCategoryTree(categories), [categories]);
    const mainCategories = useMemo(() => tree.map(({ subcategories, ...category }) => category), [tree]);
    const subcategories = useMemo(() => tree.flatMap((main) => main.subcategories.map((sub) => ({ ...sub, parentName: main.name }))), [tree]);

    const filteredSubcategories = useMemo(() => {
        const q = search.trim().toLowerCase();
        let result = subcategories;
        if (statusFilter !== 'all') {
            result = result.filter((sub) => sub.status === statusFilter);
        }
        if (filterCategory !== 'all') {
            result = result.filter((sub) => sub.parentName === filterCategory);
        }
        if (!q) return result;
        return result.filter((sub) =>
            sub.name.toLowerCase().includes(q) ||
            (sub.description || '').toLowerCase().includes(q) ||
            (sub.parentName || '').toLowerCase().includes(q)
        );
    }, [search, subcategories, statusFilter, filterCategory]);

    useEffect(() => { setPage(1); }, [search, statusFilter, filterCategory]);
    const totalPages = Math.max(1, Math.ceil(filteredSubcategories.length / pageSize));
    const paginatedRows = filteredSubcategories.slice((page - 1) * pageSize, page * pageSize);

    const save = async (payload) => {
        setActionError(null);
        const result = modal?.id
            ? await updateCategoryEntry(modal.id, payload)
            : await createSubcategoryEntry(payload);
        if (result.error) {
            setActionError(result.error);
            return;
        }
        setModal(null);
    };

    const remove = async (subcategory) => {
        if (subcategory.isLegacy) return;
        const result = await deleteCategoryEntry(subcategory.id);
        if (result.error) setActionError(result.error);
    };

    const hasActiveFilters = statusFilter !== 'all' || search.trim() !== '' || filterCategory !== 'all';

    return (
        <div className="mc-page">
            <ProductTabBar active="Manage Subcategory" />
            {loading && <div className="mc-info-banner">Loading subcategories...</div>}
            {(error || actionError) && <div className="mc-info-banner">{error || actionError}</div>}

            <div className="mc-toolbar">
                <div className="mc-toolbar-left">
                    <div className="mc-entries-label">
                        Show
                        <select className="mc-entries-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button className="mc-filter-clear" onClick={() => { setStatusFilter('all'); setSearch(''); setFilterCategory('all'); setPage(1); }}>Clear filters</button>
                    )}
                </div>
                <div className="mc-toolbar-right">
                    <div className="mc-search-wrap">
                        <input className="mc-search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search subcategories..." />
                    </div>
                    <button className="mc-btn-add" onClick={() => setModal({})} disabled={mainCategories.length === 0}>Add Subcategory</button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="mi-filter-bar">
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Category</label>
                    <select className="mi-filter-select" value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}>
                        <option value="all">All Categories</option>
                        {mainCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="mc-table-wrap">
                <table className="mc-table">
                    <thead><tr><th>Name</th><th>Description</th><th>Parent Main Category</th><th>Submenus</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {paginatedRows.length === 0 ? <tr><td colSpan={6} className="mc-empty">No subcategories found.</td></tr> : paginatedRows.map((subcategory) => (
                            <tr key={subcategory.id}>
                                <td className="mc-cat-name">{subcategory.name}</td>
                                <td>{subcategory.description || 'No description yet'}</td>
                                <td>{subcategory.parentName}</td>
                                <td>{subcategory.submenus.length}</td>
                                <td><StatusBadge status={subcategory.status} /></td>
                                <td><div className="mc-actions"><button className="mc-action-btn mc-action-btn--edit" onClick={() => setModal(subcategory)} disabled={subcategory.isLegacy} title="Edit"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button><button className="mc-action-btn mc-action-btn--delete" onClick={() => remove(subcategory)} disabled={subcategory.isLegacy} title="Delete"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button></div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredSubcategories.length > pageSize && (
                <div className="mc-pagination">
                    <select className="mc-entries-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                        {[10, 15, 20, 25].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="mc-result-count">
                        Showing {Math.min((page - 1) * pageSize + 1, filteredSubcategories.length)}–{Math.min(page * pageSize, filteredSubcategories.length)} of {filteredSubcategories.length}
                    </span>
                    <button className="mc-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                    <span className="mc-page-divider">|</span>
                    <button className="mc-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
            )}

            {modal && <SubcategoryModal subcategory={modal.id ? modal : null} mainCategories={mainCategories} onClose={() => setModal(null)} onSave={save} />}
        </div>
    );
}